import React, { useCallback, useMemo, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import ListPageLayout from "@ui-components/ListPage/ListPageLayout";
import ListPageLoading from "@ui-components/ListPage/ListPageLoading";
import ListPageToolbar from "@ui-components/ListPage/ListPageToolbar";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { backboneListColumns, type BackboneService } from "./BackboneData";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  type FilterField,
  labelId,
  parseToken,
  type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeBackboneFields } from "./backboneFieldSpec";
import {
  fetchBackboneList,
  mapBbaasItemToBackboneService,
} from "./requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import { DataStateView } from "./BackboneDetails/StateViews/DataStateView";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";

/** Rows per server page. */
const PAGE_SIZE = 10;

/** One server page of the list, as the cache keeps it. */
interface BackbonePage {
  rows: BackboneService[];
  total: number;
  hasMore: boolean;
}

const EMPTY_PAGE: BackbonePage = { rows: [], total: 0, hasMore: false };

const MainBackbone: React.FC = () => {
  const navigate = useNavigate();
  const api = useAxiosAuth0();
  
  const { tenantId, partnerId } = useAppSelector(
    (state) => state.selection
  );
  const isViewingAllCustomers = tenantId != null && tenantId === partnerId;

  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [offset, setOffset] = useState(0);

  // Another customer starts on its first page, not on whatever page the last one was left.
  const [offsetTenantId, setOffsetTenantId] = useState(tenantId);
  if (offsetTenantId !== tenantId) {
    setOffsetTenantId(tenantId);
    setOffset(0);
  }

  // Cached per tenant and server page: coming back to the list paints the last answer at
  // once and refreshes it underneath. The search bar filters client-side, so it is not
  // part of the key.
  const {
    data: page = EMPTY_PAGE,
    error: fetchError,
    isLoading,
    isRefreshing,
    reload,
  } = useCachedFetch(
    tenantId
      ? `kontroledge/bbaas?tenantId=${tenantId}&limit=${PAGE_SIZE}&offset=${offset}`
      : null,
    async () => {
      const { status, data: body } = await fetchBackboneList(api, tenantId, {
        limit: PAGE_SIZE,
        offset,
      });
      if (status >= 500 || (body && body.success === false)) {
        throw new Error(body?.message ?? "Unknown server error");
      }
      if (!body?.success || !Array.isArray(body.data)) return EMPTY_PAGE;
      return {
        rows: body.data.map(mapBbaasItemToBackboneService),
        total: body.pagination?.total ?? body.data.length,
        hasMore: body.pagination?.hasMore ?? false,
      };
    }
  );
  const backboneList = page.rows;

  // Page 1 of a tenant is always reached first, so a page with nothing cached past it is a
  // Next click: it loads inside the card (the table skeleton) instead of blanking the page.
  const isChangingPage = isLoading && offset > 0;

  const backboneFields = useMemo(
    () => makeBackboneFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const suggestedKeys = useMemo(() => {
    const headers = backboneListColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];
    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(0, 0, "Customer");
    }
    return headers;
  }, [isViewingAllCustomers]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<BackboneService>>();
    for (const f of backboneFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [backboneFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of backboneFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [backboneFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<BackboneService>(normalizedFilters, backboneFields),
    [normalizedFilters, backboneFields]
  );

  const filteredData = useMemo(
    () => backboneList.filter(predicate),
    [backboneList, predicate]
  );

  const handleBackboneIdClick = useCallback(
    (row: BackboneService) => {
      navigate(buildDetailPath("backbone", { backboneId: row.id }), {
        state: { selectedBackboneRow: row },
      });
    },
    [navigate]
  );

  const columns = useMemo<GenericTableColumn<BackboneService>[]>(() => {
    const cols = [...backboneListColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: BackboneService) => row.customer?.name ?? "",
        renderCell: (row: BackboneService) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId || "-"}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    return cols.map((col) => {
      if (col.header === "Backbone ID") {
        return {
          ...col,
          renderCell: (row: BackboneService) => (
            <button
              type="button"
              {...viewHref(buildDetailPath("backbone", { backboneId: row.id }))}
              onClick={() => handleBackboneIdClick(row)}
              className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
            >
              {row.subscriptionId ?? row.id}
            </button>
          ),
        };
      }
      return col;
    });
  }, [handleBackboneIdClick, isViewingAllCustomers]);

  const isDemo = isDemoClient();
  const backboneColumnVisibility = useColumnVisibility(
    "kontrol:backbone:columns",
    columns,
    ["Backbone ID"],
  );
  const tableColumns = isDemo ? backboneColumnVisibility.visibleColumns : columns;

  const tableColumnCount =
    backboneListColumns.length + (isViewingAllCustomers ? 1 : 0);

  // Nothing to show yet: the page's shape in skeleton, not a screen-wide overlay.
  if (isLoading && !isChangingPage) {
    return <ListPageLoading columns={tableColumnCount} />;
  }
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  if (!isChangingPage && backboneList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <ListPageLayout>
      <RefreshingIndicator active={isRefreshing} />
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              {(backboneList.length > 0 || isChangingPage) && (
                <ListPageToolbar>
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  {isDemo && (
                    <ColumnVisibilityControl
                      columns={columns}
                      isVisible={backboneColumnVisibility.isVisible}
                      toggle={backboneColumnVisibility.toggle}
                      requiredIds={["Backbone ID"]}
                    />
                  )}
                </ListPageToolbar>
              )}

              <div className="flex-1 min-h-0 flex flex-col">
                <DataStateView
                  loading={isChangingPage}
                  error={null}
                  isEmpty={false}
                  onRetry={reload}
                  loadingContent={
                    <TableSkeleton columns={tableColumnCount} rows={8} />
                  }
                >
                  <div className="flex flex-col h-full min-h-0">
                    <GenericTable<BackboneService>
                      columns={tableColumns}
                      mobileCards={{ titleColumn: "Backbone ID", badgeColumn: "Status", headlineColumn: "Name" }}
                      data={filteredData}
                      initialRowsPerPage={10}
                      rowsPerPageOptions={[10, 20, 30]}
                      width="100%"
                      tableClassName="bg-semantic-bg-primary"
                      tabletColumns={["Backbone ID", "Status", "Customer", "VRFs", "Committed"]}
                      maxTableBodyHeight="100%"
                    />
                    {page.total > 0 && (
                      <div className="flex items-center justify-between mt-2 py-2 border-t border-semantic-border-primary flex-shrink-0">
                        <span className="text-xs text-semantic-text-secondary">
                          {page.total} result{page.total !== 1 ? "s" : ""}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={offset === 0 || isLoading}
                            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                            className="px-2 py-1 rounded text-xs font-medium bg-semantic-bg-primary text-semantic-text-primary border border-semantic-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-semantic-state-active"
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            disabled={!page.hasMore || isLoading}
                            onClick={() => setOffset((o) => o + PAGE_SIZE)}
                            className="px-2 py-1 rounded text-xs font-medium bg-semantic-bg-primary text-semantic-text-primary border border-semantic-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-semantic-state-active"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </DataStateView>
              </div>
            </div>
          }
        />
      </div>
    </ListPageLayout>
  );
};

export default MainBackbone;
